import './index.css'

import debounce from 'lodash/debounce'
import React, { useCallback, useMemo, useRef } from 'react'
import { FixedSizeList as List } from 'react-window'
import InfiniteLoader from 'react-window-infinite-loader'

import type { Contact, RecipientList } from 'types/list'

import Skeleton from 'components/ui/skeleton'

import ContactsMenuAddButtons from 'pages/contacts/add/buttons'

import ContactHeader from '../contact-header'
import ContactRow from '../contact-row'
import ContactsMessage from '../contacts-message'
import DefaultRow from '../default-row'
import ReactWindowScroller from './react-window-scroller'

type Props = {
  // TODO: Fix type
  headers?: any
  columns: any
  sortContacts?: (...args: Array<any>) => any
  onRowModeSwitch?: (...args: Array<any>) => any
  onSelect?: (...args: Array<any>) => any
  onFieldChange?: (...args: Array<any>) => any
  onFieldValueAdd?: (...args: Array<any>) => any
  onFieldValueRemove?: (...args: Array<any>) => any
  onSelectAll?: (...args: Array<any>) => any
  sortParam?: string
  sortDirection?: string
  searchTerm?: string
  emptyText?: React.ReactNode
  beingSearched?: boolean
  appendingContacts?: boolean
  list?: RecipientList | null | undefined
  truncationLimit?: number
  contacts: Contact[]
  rowRenderer?: string | null | undefined
  loading?: boolean
  loaded?: boolean
  error?: string | null | undefined
  loadContacts?: (...args: Array<any>) => any
  contactsCount?: number
}

type RowData = {
  contacts: Contact[]
  columns: Props['columns']
  list: Props['list']
  rowRenderer: Props['rowRenderer']
  onRowModeSwitch: Props['onRowModeSwitch']
  appendingContacts: Props['appendingContacts']
  onFieldChange: Props['onFieldChange']
  onFieldValueAdd: Props['onFieldValueAdd']
  onFieldValueRemove: Props['onFieldValueRemove']
  onSelect: Props['onSelect']
  searchTerm: Props['searchTerm']
  sortParam: Props['sortParam']
  sortDirection: Props['sortDirection']
  batchMode: boolean
}

const ContactsTable = ({
  headers,
  columns,
  sortContacts,
  onRowModeSwitch,
  onSelect,
  onFieldChange,
  onFieldValueAdd,
  onFieldValueRemove,
  onSelectAll,
  sortParam,
  sortDirection,
  searchTerm,
  emptyText,
  beingSearched,
  appendingContacts,
  list,
  truncationLimit,
  contacts,
  rowRenderer,
  loading,
  loaded,
  error,
  loadContacts,
  contactsCount,
}: Props): React.ReactNode | React.ReactElement<'div'> => {
  const contactsToLoad = useRef<{
    startIndex: number | null
    stopIndex: number | null
  }>({
    startIndex: null,
    stopIndex: null,
  })
  const selectedAll = loaded
    ? !contacts.filter(c => c.loaded).some(c => !c.selected)
    : false
  const showTable = (loaded || rowRenderer === 'default') && !!contacts.length
  const handleLoadContacts = useCallback(
    (startIndex: number, stopIndex: number) => {
      if (loadContacts) {
        loadContacts(startIndex, stopIndex + 1)
        contactsToLoad.current = {
          startIndex: null,
          stopIndex: null,
        }
      }
    },
    [loadContacts],
  )
  const debouncedLoadContacts = useCallback(
    debounce((startIndex: number, stopIndex: number) => {
      handleLoadContacts(startIndex, stopIndex)
    }, 200),
    [handleLoadContacts],
  )

  const loadMoreRows = (startIndex: number, stopIndex: number) => {
    const { startIndex: prevStartIndex, stopIndex: prevStopIndex } =
      contactsToLoad.current
    let newStartIndex = startIndex

    if (typeof prevStartIndex === 'number' && prevStartIndex < startIndex) {
      newStartIndex = prevStartIndex
    }

    let newStopIndex = stopIndex

    if (typeof prevStopIndex === 'number') {
      if (stopIndex > prevStopIndex) {
        newStopIndex = stopIndex
      } else {
        newStopIndex = prevStopIndex
      }
    }

    contactsToLoad.current = {
      startIndex: newStartIndex,
      stopIndex: newStopIndex,
    }

    if (typeof newStartIndex === 'number' && typeof newStopIndex === 'number') {
      if (newStopIndex - newStartIndex > 100) {
        handleLoadContacts(newStartIndex, newStopIndex)
      } else {
        debouncedLoadContacts(newStartIndex, newStopIndex)
      }
    }
  }

  // Identity-stable ([] deps) so react-window never treats a parent render as a
  // new row component type (which would remount every row). All inputs come from
  // `data` (the list's itemData), so nothing here closes over the parent scope.
  const renderRow = useCallback(
    ({
      index,
      style,
      data,
    }: {
      index: number
      style?: React.CSSProperties
      data: RowData
    }): React.ReactNode => {
      const {
        contacts,
        columns,
        list,
        rowRenderer,
        onRowModeSwitch,
        appendingContacts,
        onFieldChange,
        onFieldValueAdd,
        onFieldValueRemove,
        onSelect,
      } = data
      const contact = contacts[index]
      if (!contact) return null
      if (!contact?.loaded && !(rowRenderer === 'default'))
        return (
          <div key={`contact-${index}`} style={style}>
            <div className="ContactRow">
              <div className="ContactCell">
                <Skeleton />
              </div>
            </div>
          </div>
        )
      return rowRenderer === 'default' ? (
        <div key={`contact-${contact.id}`} style={style}>
          <DefaultRow
            columns={columns}
            contact={contact}
            list={list}
            onRowModeSwitch={onRowModeSwitch}
            appendingContacts={appendingContacts}
            onFieldChange={onFieldChange}
            onFieldValueAdd={onFieldValueAdd}
            onFieldValueRemove={onFieldValueRemove}
          />
        </div>
      ) : (
        <div key={`contact-${contact.id}`} style={style}>
          <ContactRow
            columns={columns}
            contactId={contact.id}
            list={list}
            onRowModeSwitch={onRowModeSwitch}
            appendingContacts={appendingContacts}
            onFieldChange={onFieldChange}
            onFieldValueAdd={onFieldValueAdd}
            onFieldValueRemove={onFieldValueRemove}
            onSelect={onSelect}
          />
        </div>
      )
    },
    [],
  )

  const isItemLoaded = (index: number) => !!contacts[index]?.loaded

  const batchMode = contacts.filter(c => c.editMode).length > 1

  // renderRow reads its inputs from this object (react-window passes it as the
  // row's `data`), so it must include every row-affecting value — react-window
  // only re-renders rows when itemData changes, and anything omitted goes stale.
  const itemData = useMemo<RowData>(
    () => ({
      contacts,
      columns,
      list,
      rowRenderer,
      onRowModeSwitch,
      appendingContacts,
      onFieldChange,
      onFieldValueAdd,
      onFieldValueRemove,
      onSelect,
      searchTerm,
      sortParam,
      sortDirection,
      batchMode,
    }),
    [
      contacts,
      columns,
      list,
      rowRenderer,
      onRowModeSwitch,
      appendingContacts,
      onFieldChange,
      onFieldValueAdd,
      onFieldValueRemove,
      onSelect,
      searchTerm,
      sortParam,
      sortDirection,
      batchMode,
    ],
  )

  const renderTable = () => {
    if (truncationLimit) {
      let note = null

      if (contacts.length >= truncationLimit) {
        const more = contacts.length - truncationLimit
        note = (
          <div className="ContactRow">
            <div className="ContactsTable_Truncated">
              and {more} more <span>{more === 1 ? 'contact' : 'contacts'}</span>
            </div>
          </div>
        )
      }

      return (
        <div>
          {contacts.slice(0, truncationLimit).map((_, index) =>
            renderRow({
              index,
              data: itemData,
            }),
          )}
          {note}
        </div>
      )
    } else if (rowRenderer === 'default') {
      return (
        <ReactWindowScroller>
          {({ ref, outerRef, style, height, onScroll }) => (
            <List
              ref={ref}
              outerRef={outerRef}
              style={style}
              width="100%"
              height={height}
              itemCount={contacts.length}
              itemSize={40}
              overscanCount={30}
              onScroll={onScroll}
              itemData={itemData}
            >
              {renderRow}
            </List>
          )}
        </ReactWindowScroller>
      )
    } else {
      // Fall back to the loaded rows when the server count isn't known yet;
      // itemCount={0} would stop InfiniteLoader from ever loading (SLAM-1970).
      const itemCount = contactsCount ?? contacts.length
      return (
        <InfiniteLoader
          isItemLoaded={isItemLoaded}
          loadMoreItems={loadMoreRows}
          itemCount={itemCount}
          minimumBatchSize={1}
          threshold={0}
        >
          {({ onItemsRendered, ref: infiniteLoaderRef }) => (
            <ReactWindowScroller listRef={infiniteLoaderRef}>
              {({ ref, outerRef, style, height, onScroll }) => (
                <List
                  ref={ref}
                  outerRef={outerRef}
                  style={style}
                  width="100%"
                  height={height}
                  itemCount={itemCount}
                  itemSize={40}
                  overscanCount={30}
                  onItemsRendered={onItemsRendered}
                  onScroll={onScroll}
                  itemData={itemData}
                >
                  {renderRow}
                </List>
              )}
            </ReactWindowScroller>
          )}
        </InfiniteLoader>
      )
    }
  }

  if (error) {
    return (
      <div className="ContactsTable_Empty">
        <ContactsMessage iconType="error" header="Failed to load contacts" />
      </div>
    )
  }

  if (
    (searchTerm || beingSearched) &&
    contacts.length === 0 &&
    !loading &&
    loaded
  ) {
    return (
      <div className="ContactsTable_Empty">
        {emptyText || (
          <ContactsMessage
            iconType="search"
            header={emptyText || 'Sorry! We couldn’t find any contacts'}
            message="Try entering another search term"
          />
        )}
      </div>
    )
  }

  if (contacts.length === 0 && appendingContacts && !loading && loaded) {
    return (
      <div className="ContactsTable_Empty">
        <ContactsMessage
          iconType="search"
          header="Good news! All your contacts are in this list 👍"
        />
      </div>
    )
  }

  if (contacts.length === 0 && !loading && loaded) {
    return (
      <div className="ContactsTable_Empty">
        <ContactsMessage iconType="people" header="No contacts yet">
          <ContactsMenuAddButtons list={list} />
        </ContactsMessage>
      </div>
    )
  }

  return (
    <>
      <ContactHeader
        columns={headers || columns}
        sortContacts={sortContacts}
        sortParam={sortParam}
        sortDirection={sortDirection}
        selectedAll={selectedAll}
        onSelectAll={onSelectAll}
        disabled={loading}
      />
      <div className="ContactsTable">{showTable && renderTable()}</div>
      {!showTable && <div className="ContactsTable-Loader">Loading...</div>}
    </>
  )
}

export default ContactsTable
